/**
 * Plain-language explainers for the formula settings the setup wizard asks
 * about. Shared so the web disclosures and the mobile info sheets say the
 * same thing. Keep each body to two or three sentences: it is read on a phone.
 */

export interface SetupExplainer {
  title: string;
  body: string;
}

export const SETUP_EXPLAINERS = {
  travelTime: {
    title: 'Travel time rate',
    body: 'Fixed bills a set number of travel hours on every job (the Fixed Travel Time Hours), once per shift, wherever the job is. Portal to Portal bills the crew’s real drive between your nearest warehouse and the job, so it needs a warehouse address: with no warehouse it silently bills 0 travel hours.',
  },
  localMoveRadius: {
    title: 'Local move radius',
    body: 'Decides which moves the app pre-labels Local on Key Dates & Addresses: a drive from origin to destination within this distance (or time) is Local, beyond it In-State. It only sets that label, which the estimator can change per quote. It does not change the price.',
  },
  shifts: {
    title: 'Shifts',
    body: 'A shift is the hours one crew works in a day. The formula divides the job’s on-site hours by the shift length to plan how many shifts (days) the move takes, and travel time is billed once per shift. Shorter shifts mean more days and more travel charges.',
  },
  itemization: {
    title: 'Itemization preference',
    body: 'How the client quote breaks the price down. Single Line shows one “Moving Services” total; Crew & Trucks shows a line per crew role and truck at its hourly rate; Duration & Transit splits inventory time, crew travel time and transit over one hour. Materials and the scope of services are separate toggles under Quoting → Quote Defaults.',
  },
  quoteValidity: {
    title: 'Quote validity',
    body: 'How many days a client quote stays open after it is generated. After that the online quote shows as expired and the estimator generates a new version. The estimator can pick a different expiration on each quote.',
  },
  efficiencyMultiplier: {
    title: 'Efficiency multiplier',
    body: 'Scales every calculated job duration. 1 uses the IOMI tables as they are; 1.1 adds 10% for a slower crew or awkward buildings; 0.9 trims 10%.',
  },
} as const satisfies Record<string, SetupExplainer>;

export type SetupExplainerKey = keyof typeof SETUP_EXPLAINERS;

/** Shown wherever Portal to Portal is selected while the org has no warehouse. */
export const PORTAL_TO_PORTAL_NO_WAREHOUSE_WARNING =
  'Portal to Portal travel time is selected, but there is no warehouse yet. Until you add one, every quote bills 0 hours of travel.';

/** What each setup section still needs, for the wizard’s "Almost there" recap. */
export const SETUP_SECTION_REMAINING: Record<string, string> = {
  companyProfileCompleted: 'Legal name and business email',
  moveTypesCompleted: 'Turn on commercial or residential quotes',
  fleetCompleted: 'Add at least one warehouse',
  residentialSettingsCompleted: 'Crew rates (or every flat-rate tier) above $0',
  commercialSettingsCompleted: 'Crew and truck rates above $0',
  paymentsCompleted: 'Decide whether to collect customer payments',
  materialsPricingCompleted: 'Review the unit cost and rental length of your materials',
};
